import { create } from 'zustand'
import update from 'immutability-helper'
import { clone, some } from 'lodash-es'

import { reorder } from '@/helpers/dnd'
import type { TableColumn, TableData, TableFilter } from '@/types/Table'

interface TableState {
  tables: TableData[]
  initTable: (variables: {
    tableName: string
    sortKey: string
    config: Partial<TableColumn>
    columns: TableColumn[]
    allColumns: TableColumn[]
    filters: TableFilter[]
  }) => void
  sortTable: (
    tableName: string,
    sortKey: string,
    sortDirection: 'asc' | 'desc',
  ) => void
  searchTable: (tableName: string, search: string) => void
  addFilter: (tableName: string, key: string, value: any) => void
  removeFilter: (tableName: string, key: string, value: any) => void
  invertFilter: (tableName: string, key: string, value: any) => void
  toggleFilterMode: (tableName: string, key: string, value: boolean) => void
  toggleMultiColumnFilterMode: (tableName: string, value: boolean) => void
  removeAllFilters: (tableName: string) => void
  moveColumn: (tableName: string, oldIndex: number, newIndex: number) => void
  updateColumns: (tableName: string, columns: TableColumn[]) => void
  updateFilters: (tableName: string, filters: TableFilter[]) => void
  check: (tableName: string, values: any) => void
  clearChecked: (tableName: string) => void
}

export const columnDefaults = (column: TableColumn): TableColumn =>
  Object.assign(
    {
      sortable: false,
      searchable: false,
      checkbox: false,
      textAlign: undefined,
      clickable: true,
      onCheckAllQuery: null,
      searchMappings: [],
      filters: [],
      verticalAlign: 'middle',
      width: 'auto',
      help: '',
      title: '',
      sortKey: column.sortKey || column.key,
      isArray: false,
      isInterval: false,
      type: null,
      metadata: null,
    },
    column,
  )

export const mapColumns = (columns: TableColumn[]): TableColumn[] =>
  columns.map(c => ({
    __typename: 'TableColumn',
    ...columnDefaults(c),
  }))

const useTableStore = create<TableState>(set => ({
  tables: [],
  initTable: ({
    tableName,
    sortKey,
    config,
    columns = [],
    allColumns = [],
    filters = [],
  }) =>
    set(state => {
      const isMobile = window.innerWidth < 767

      const newTable: TableData = {
        __typename: 'Table',
        columns: mapColumns(columns),
        allColumns: mapColumns(allColumns),
        sortKey: sortKey || '',
        sortDirection: 'asc',
        search: '',
        help: '',
        page: 0,
        pageSize: isMobile ? 10 : 20,
        searchableTableColumns: [],
        fullTextSearchTableColumns: [],
        checked: [],
        matchAllColumns: true,
        // @ts-ignore
        filters,
        tableName,
        ...config,
      }

      return {
        tables: [...state.tables, newTable],
      }
    }),
  sortTable: (tableName, sortKey, sortDirection) =>
    set(state => {
      const index = state.tables.findIndex(x => x.tableName === tableName)
      return {
        tables: update(state.tables, {
          [index]: {
            $merge: { sortKey, sortDirection },
          },
        }),
      }
    }),
  searchTable: (tableName, search) =>
    set(state => {
      const index = state.tables.findIndex(x => x.tableName === tableName)

      const newTables = state.tables.map(clone)

      newTables[index].search = search

      return {
        tables: newTables,
      }
    }),
  addFilter: (tableName, key, value) =>
    set(state => {
      const index = state.tables.findIndex(x => x.tableName === tableName)

      const filterIndex = state.tables[index].filters.findIndex(
        x => x.field === key,
      )

      const columnIndex = state.tables[index].columns.findIndex(
        (x: any) => x.key === key || x.filters.filterKey === key,
      )

      let newFilters: TableFilter[] = []

      // Update array in object without mutation:
      // https://stackoverflow.com/questions/42053178/update-array-of-object-without-mutation
      // There is probably a more elegant way than this...
      if (filterIndex !== -1) {
        newFilters = [
          ...state.tables[index].filters.slice(0, filterIndex),
          {
            ...state.tables[index].filters[filterIndex],
            queries: [
              ...state.tables[index].filters[filterIndex].queries,
              value,
            ],
            negativeQueries: [
              ...state.tables[index].filters[filterIndex].negativeQueries,
            ],
          },
          ...state.tables[index].filters.slice(filterIndex + 1),
        ]
      } else {
        newFilters = [
          ...state.tables[index].filters,
          {
            field:
              state.tables[index].columns?.[columnIndex]?.filters?.filterKey ||
              key,
            queries: [value],
            negativeQueries: [],
            matchAll: false,
            isArray: state.tables[index].columns[columnIndex].isArray,
            isEnum: state.tables[index].columns[columnIndex].isEnum,
            isInterval:
              !!state.tables[index].columns?.[columnIndex]?.filters
                ?.addIntervalFilter,
          },
        ]
      }

      return {
        tables: [
          ...state.tables.slice(0, index),
          { ...state.tables[index], filters: newFilters },
          ...state.tables.slice(index + 1),
        ],
      }
    }),
  removeFilter: (tableName, key, value) =>
    set(state => {
      const index = state.tables.findIndex(x => x.tableName === tableName)

      const filterIndex = state.tables[index].filters.findIndex(
        x => x.field === key,
      )

      if (filterIndex !== -1) {
        // Update array in object without mutation:
        // https://stackoverflow.com/questions/42053178/update-array-of-object-without-mutation
        // There is probably a more elegant way than this...
        const newFilters = [
          ...state.tables[index].filters.slice(0, filterIndex),
          {
            ...state.tables[index].filters[filterIndex],
            queries: state.tables[index].filters[filterIndex].queries.filter(
              x => x !== value,
            ),
            negativeQueries: state.tables[index].filters[
              filterIndex
            ].negativeQueries.filter(x => x !== value),
          },
          ...state.tables[index].filters.slice(filterIndex + 1),
        ].filter(
          newFilter =>
            newFilter.queries.length > 0 ||
            newFilter.negativeQueries.length > 0,
        )

        return {
          tables: [
            ...state.tables.slice(0, index),
            { ...state.tables[index], filters: newFilters },
            ...state.tables.slice(index + 1),
          ],
        }
      }
      return { tables: state.tables }
    }),
  invertFilter: (tableName, key, value) =>
    set(state => {
      const index = state.tables.findIndex(x => x.tableName === tableName)

      const filterIndex = state.tables[index].filters.findIndex(
        x => x.field === key,
      )

      const negativeFilterValueIndex = state.tables[index].filters[
        filterIndex
      ].negativeQueries.findIndex(x => x === value)

      let newFilters: TableFilter[] = []

      // Update array in object without mutation:
      // https://stackoverflow.com/questions/42053178/update-array-of-object-without-mutation
      // There is probably a more elegant way than this...
      if (filterIndex !== -1) {
        newFilters = [
          ...state.tables[index].filters.slice(0, filterIndex),
          {
            ...state.tables[index].filters[filterIndex],
            queries: [...state.tables[index].filters[filterIndex].queries],
            negativeQueries:
              negativeFilterValueIndex === -1
                ? [
                    ...state.tables[index].filters[filterIndex].negativeQueries,
                    value,
                  ]
                : [
                    ...state.tables[index].filters[
                      filterIndex
                    ].negativeQueries.filter(query => query !== value),
                  ],
          },
          ...state.tables[index].filters.slice(filterIndex + 1),
        ]
      }

      return {
        tables: [
          ...state.tables.slice(0, index),
          { ...state.tables[index], filters: newFilters },
          ...state.tables.slice(index + 1),
        ],
      }
    }),
  toggleFilterMode: (tableName, key, value) =>
    set(state => {
      const index = state.tables.findIndex(x => x.tableName === tableName)

      const filterIndex = state.tables[index].filters.findIndex(
        x => x.field === key,
      )

      if (filterIndex !== -1) {
        // Update array in object without mutation:
        // https://stackoverflow.com/questions/42053178/update-array-of-object-without-mutation
        // There is probably a more elegant way than this...
        const newFilters = [
          ...state.tables[index].filters.slice(0, filterIndex),
          {
            ...state.tables[index].filters[filterIndex],
            matchAll: value,
          },
          ...state.tables[index].filters.slice(filterIndex + 1),
        ]

        return {
          tables: [
            ...state.tables.slice(0, index),
            { ...state.tables[index], filters: newFilters },
            ...state.tables.slice(index + 1),
          ],
        }
      }

      return { tables: state.tables }
    }),
  toggleMultiColumnFilterMode: (tableName, value) =>
    set(state => {
      const index = state.tables.findIndex(x => x.tableName === tableName)
      return {
        tables: [
          ...state.tables.slice(0, index),
          { ...state.tables[index], matchAllColumns: value },
          ...state.tables.slice(index + 1),
        ],
      }
    }),
  removeAllFilters: tableName =>
    set(state => {
      const index = state.tables.findIndex(x => x.tableName === tableName)
      return {
        tables: [
          ...state.tables.slice(0, index),
          { ...state.tables[index], filters: [] },
          ...state.tables.slice(index + 1),
        ],
      }
    }),
  moveColumn: (tableName, oldIndex, newIndex) =>
    set(state => {
      const index = state.tables.findIndex(x => x.tableName === tableName)
      const newTables = state.tables.map(clone)

      newTables[index].columns = reorder(
        newTables[index].columns,
        oldIndex,
        newIndex,
      )

      return {
        tables: update(state.tables, {
          $set: newTables,
        }),
      }
    }),
  updateColumns: (tableName, columns) =>
    set(state => {
      const index = state.tables.findIndex(x => x.tableName === tableName)
      const newTables = state.tables.map(clone)

      newTables[index].columns = mapColumns(columns.filter(x => !!x))

      // Remove filters from deselected columns
      newTables[index].filters = newTables[index].filters.filter(({ field }) =>
        some(newTables[index].columns, { key: field }),
      )

      return {
        tables: update(state.tables, {
          $set: newTables,
        }),
      }
    }),
  updateFilters: (tableName, filters) =>
    set(state => {
      const index = state.tables.findIndex(x => x.tableName === tableName)
      const newTables = state.tables.map(clone)
      newTables[index].filters = filters ?? []

      return {
        tables: update(state.tables, {
          $set: newTables,
        }),
      }
    }),
  check: (tableName, values) =>
    set(state => {
      const index = state.tables.findIndex(x => x.tableName === tableName)
      const newTables = state.tables.map(clone)
      values.forEach((v: any) => {
        if (!newTables[index].checked.includes(v)) {
          newTables[index].checked = [...newTables[index].checked, v]
        } else {
          const valueIndex = newTables[index].checked.indexOf(v)
          newTables[index].checked = [
            ...newTables[index].checked.slice(0, valueIndex),
            ...newTables[index].checked.slice(valueIndex + 1),
          ]
        }
      })
      return {
        tables: update(state.tables, {
          $set: newTables,
        }),
      }
    }),
  clearChecked: tableName =>
    set(state => {
      const index = state.tables.findIndex(x => x.tableName === tableName)
      return {
        tables: update(state.tables, {
          [index]: {
            $set: { ...state.tables[index], checked: [] },
          },
        }),
      }
    }),
}))

export default useTableStore
